/** @jsxImportSource @emotion/react */
import React from "react";
import {
  Alert,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Button,
  Select,
  InputNumber,
  Typography,
} from "antd";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Styles from "./styles";
import { HomeUsecase, useHomeDispatch } from "../../../usecases/Home";
import { CreateHomeDto } from "../../../services/Home/dto/create.dto";
import { homeFormSchema } from "./formSchema";
import { useSettingState } from "../../../usecases/Setting";
import Dropzone, { DropzoneRef, useDropzone } from "react-dropzone";
import ReactAvatarEditor from "react-avatar-editor";
import AvatarEditor from "react-avatar-editor";

interface ModalAddHomeProps {
  onCancel: () => void;
  onSuccess: () => void;
  visible: boolean;
}

const ModalAddHomeComponent: React.FC<ModalAddHomeProps> = ({
  visible,
  onCancel,
  onSuccess,
}: ModalAddHomeProps) => {
  const [tempFileType, setTempFileType] = React.useState("");
  const [selectedFloorplanMap, setSelectedFloorplanMap] = React.useState();
  const [editor, setEditor] = React.useState<AvatarEditor>();
  useDropzone({
    // Disable click and keydown behavior
    noClick: true,
    noKeyboard: true,
  });
  const dropzoneRef = React.createRef<DropzoneRef>();

  const settingState = useSettingState();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setValue,
  } = useForm<CreateHomeDto>({
    mode: "onSubmit",
    resolver: yupResolver(homeFormSchema),
  });
  const homeDispatch = useHomeDispatch();

  const handleSubmitHandle = React.useCallback(
    async (data: CreateHomeDto) => {
      setIsProcessing(true);
      try {
        if (homeDispatch) {
          const homeUsecase = new HomeUsecase(homeDispatch);
          await homeUsecase.create({
            ...data,
            homeBuilderId: settingState.menuDetail?.id,
          });
        }
        reset();
        setSelectedFloorplanMap(undefined);
        setErrorMessage("");
        onSuccess();
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message);
      } finally {
        setIsProcessing(false);
      }
    },
    [homeDispatch, reset, onSuccess, settingState.menuDetail?.id],
  );

  const onCancelHandle = React.useCallback(() => {
    reset();
    setSelectedFloorplanMap(undefined);
    setErrorMessage("");
    onCancel();
  }, [onCancel, reset]);

  const getImageBase64 = React.useCallback(
    (type: string) => {
      return new Promise<string>((resolve, reject) => {
        try {
          if (editor) {
            const canvas = editor.getImage();
            const base64 = canvas.toDataURL(type).split(",")[1];
            resolve(base64);
          }
          resolve("");
        } catch (error) {
          reject(error);
        }
      });
    },
    [editor],
  );

  const avatarEditorRefHandler = React.useCallback((instance) => {
    setEditor(instance);
  }, []);

  const handleDrop = React.useCallback((dropped) => {
    if (dropped.length) {
      const file = dropped[0];
      setTempFileType(file.type);
      setSelectedFloorplanMap(file);
    }
  }, []);

  const onFloorplanMapLoadSuccess = React.useCallback(async () => {
    if (tempFileType) {
      const base64Image = await getImageBase64(tempFileType);
      setValue("homeFloorplanMapBase64", base64Image);
    }
  }, [getImageBase64, tempFileType, setValue]);

  return (
    <Modal
      title="Add New Home"
      visible={visible}
      closable={false}
      footer={[
        <Button key="back" size="large" onClick={onCancelHandle}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          size="large"
          loading={isProcessing}
          onClick={handleSubmit(handleSubmitHandle)}
        >
          Add
        </Button>,
      ]}
    >
      <Form layout="vertical" onFinish={handleSubmit(handleSubmitHandle)}>
        {errorMessage && (
          <Row justify="center">
            <Col css={Styles.colGutterStyle}>
              <Alert message={errorMessage} type="error" />
            </Col>
          </Row>
        )}
        <Row gutter={16}>
          <Col xs={8}>
            <Form.Item label="Floorplan Map" css={Styles.labelStyle}>
              <div css={Styles.imageContainerStyle}>
                <ReactAvatarEditor
                  ref={avatarEditorRefHandler}
                  image={selectedFloorplanMap || ""}
                  width={128}
                  height={128}
                  border={0}
                  scale={1}
                  borderRadius={0}
                  onLoadSuccess={onFloorplanMapLoadSuccess}
                  css={Styles.imageEditorStyle}
                />
                <Dropzone onDrop={handleDrop} ref={dropzoneRef}>
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()} css={Styles.imageEditorInput}>
                      {tempFileType === undefined ? (
                        <Typography.Paragraph>Upload</Typography.Paragraph>
                      ) : null}
                      <input {...getInputProps()} />
                    </div>
                  )}
                </Dropzone>
              </div>
            </Form.Item>
          </Col>
          <Col xs={16}>
            <Form.Item
              label="Name"
              css={Styles.labelStyle}
              validateStatus={errors.homeName ? "error" : "validating"}
              help={errors.homeName?.message}
            >
              <Controller
                name="homeName"
                control={control}
                render={({ field }) => <Input {...field} size="large" />}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={12}>
            <Form.Item
              label="Range"
              css={Styles.labelStyle}
              validateStatus={errors.homeRange ? "error" : "validating"}
              help={errors.homeName?.message}
            >
              <Controller
                name="homeRange"
                control={control}
                render={({ field }) => (
                  <Select {...field} size="large">
                    <Select.Option value="range1">Range 1</Select.Option>
                    <Select.Option value="range2">Range 2</Select.Option>
                  </Select>
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              label="Storey"
              css={Styles.labelStyle}
              validateStatus={errors.homeStorey ? "error" : "validating"}
              help={errors.homeStorey?.message}
            >
              <Controller
                name="homeStorey"
                control={control}
                render={({ field }) => (
                  <Select {...field} size="large">
                    <Select.Option value="single">Single</Select.Option>
                    <Select.Option value="double">Double</Select.Option>
                  </Select>
                )}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={8}>
            <Form.Item
              label="Number of Bed"
              css={Styles.labelStyle}
              validateStatus={errors.homeBedNo ? "error" : "validating"}
              help={errors.homeBedNo?.message}
            >
              <Controller
                name="homeBedNo"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    size="large"
                    css={Styles.fullWidthNumberInputStyle}
                  />
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item
              label="Number of Bath"
              css={Styles.labelStyle}
              validateStatus={errors.homeBathNo ? "error" : "validating"}
              help={errors.homeBathNo?.message}
            >
              <Controller
                name="homeBathNo"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    size="large"
                    css={Styles.fullWidthNumberInputStyle}
                  />
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item
              label="Number of Garage"
              css={Styles.labelStyle}
              validateStatus={errors.homeGarageNo ? "error" : "validating"}
              help={errors.homeGarageNo?.message}
            >
              <Controller
                name="homeGarageNo"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    size="large"
                    css={Styles.fullWidthNumberInputStyle}
                  />
                )}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Level"
          css={Styles.labelStyle}
          validateStatus={errors.homeLevelPath ? "error" : "validating"}
          help={errors.homeLevelPath?.message}
        >
          <Controller
            name="homeLevelPath"
            control={control}
            render={({ field }) => <Input {...field} size="large" />}
          />
        </Form.Item>
        <Row gutter={16}>
          <Col xs={8}>
            <Form.Item
              label="From Price"
              css={Styles.labelStyle}
              validateStatus={errors.homeFromPrice ? "error" : "validating"}
              help={errors.homeFromPrice?.message}
            >
              <Controller
                name="homeFromPrice"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    size="large"
                    css={Styles.fullWidthNumberInputStyle}
                  />
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item
              label="To Price"
              css={Styles.labelStyle}
              validateStatus={errors.homeToPrice ? "error" : "validating"}
              help={errors.homeToPrice?.message}
            >
              <Controller
                name="homeToPrice"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    size="large"
                    css={Styles.fullWidthNumberInputStyle}
                  />
                )}
              />
            </Form.Item>
          </Col>
          <Col xs={8}>
            <Form.Item
              label="Lot Size"
              css={Styles.labelStyle}
              validateStatus={errors.homeLotSize ? "error" : "validating"}
              help={errors.homeLotSize?.message}
            >
              <Controller
                name="homeLotSize"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    {...field}
                    size="large"
                    css={Styles.fullWidthNumberInputStyle}
                  />
                )}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export const ModalAddHome = React.memo(ModalAddHomeComponent);
